import { Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '../layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleGuard } from './RoleGuard';
import { ForbiddenPage, NotFoundPage } from './ForbiddenPage';
import { LoginPage } from '@features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@features/auth/pages/ResetPasswordPage';
import { DashboardPage } from '@features/dashboard/pages/DashboardPage';
import { ContentPerformancePage } from '@features/analytics/pages/ContentPerformancePage';
import { ProfilePage } from '@features/profile/pages/ProfilePage';
import { AdminUsersPage } from '@features/users/pages/AdminUsersPage';
import { RolesPage } from '@features/users/pages/RolesPage';
import { AuditLogPage } from '@features/audit/pages/AuditLogPage';
import { CategoriesPage } from '@features/categories/pages/CategoriesPage';
import { TagsPage } from '@features/tags/pages/TagsPage';
import { AuthorsPage } from '@features/authors/pages/AuthorsPage';
import { TranslationsPage } from '@features/translations/pages/TranslationsPage';
import { LanguagesPage } from '@features/languages/pages/LanguagesPage';
import { IpAllowlistPage } from '@features/ip-allowlist/pages/IpAllowlistPage';
import { SssListPage } from '@features/sss/pages/SssListPage';
import { SssEditPage } from '@features/sss/pages/SssEditPage';
import { SssAnalyticsPage } from '@features/sss/pages/SssAnalyticsPage';
import { ArticleListPage } from '@features/articles/pages/ArticleListPage';
import { ArticleEditPage } from '@features/articles/pages/ArticleEditPage';
import { CampaignListPage } from '@features/campaigns/pages/CampaignListPage';
import { CampaignEditPage } from '@features/campaigns/pages/CampaignEditPage';
import { WorkflowQueuePage } from '@features/workflow/pages/WorkflowQueuePage';
import { MenusPage } from '@features/menus/pages/MenusPage';
import { MenuEditorPage } from '@features/menus/pages/MenuEditorPage';
import { SlidersPage } from '@features/widgets/pages/SlidersPage';
import { BannersPage } from '@features/widgets/pages/BannersPage';
import { PopupsPage } from '@features/widgets/pages/PopupsPage';
import { FormsPage } from '@features/forms/pages/FormsPage';
import { FormEditorPage } from '@features/forms/pages/FormEditorPage';
import { FormSubmissionsPage } from '@features/forms/pages/FormSubmissionsPage';
import { ComponentsPage } from '@features/component-studio/pages/ComponentsPage';
import { ComponentEditorPage } from '@features/component-studio/pages/ComponentEditorPage';
import { BranchListPage } from '@features/branches/pages/BranchListPage';
import { BranchEditPage } from '@features/branches/pages/BranchEditPage';
import { RedirectMapPage } from '@features/web/pages/RedirectMapPage';
import { OgPreviewPage } from '@features/web/pages/OgPreviewPage';
import { SeoSettingsPage } from '@features/web/pages/SeoSettingsPage';
import { PageListPage } from '@features/pages/pages/PageListPage';
import { PageEditPage } from '@features/pages/pages/PageEditPage';
import { KvkkDeletionQueuePage } from '@features/kvkk/pages/KvkkDeletionQueuePage';
import { MyKvkkRequestPage } from '@features/kvkk/pages/MyKvkkRequestPage';
import { SettingsPage } from '@features/settings/pages/SettingsPage';
import { SmtpSettingsPage } from '@features/smtp/pages/SmtpSettingsPage';
import { LdapSettingsPage } from '@features/ldap/pages/LdapSettingsPage';
import { ApiKeysPage } from '@features/api-keys/pages/ApiKeysPage';
import { ProjectsPage } from '@features/projects/pages/ProjectsPage';
import { MediaCenterPage } from '@features/media/pages/MediaCenterPage';
import { ROUTES } from '@shared/constants/routes';
import { PERMISSIONS } from '@shared/rbac/permissions.const';

export const routes: RouteObject[] = [
  { path: ROUTES.LOGIN, element: <LoginPage /> },
  { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
  { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
          { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
          {
            path: ROUTES.ANALYTICS_CONTENT,
            element: (
              <RoleGuard permission={PERMISSIONS.DASHBOARD_VIEW}>
                <ContentPerformancePage />
              </RoleGuard>
            ),
          },

          {
            path: ROUTES.ARTICLES,
            element: (
              <RoleGuard permission={PERMISSIONS.ARTICLE_VIEW}>
                <ArticleListPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.ARTICLE_NEW,
            element: (
              <RoleGuard permission={PERMISSIONS.ARTICLE_CREATE}>
                <ArticleEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.ARTICLE_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.ARTICLE_VIEW}>
                <ArticleEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SSS_ANALYTICS,
            element: (
              <RoleGuard permission={PERMISSIONS.SSS_VIEW}>
                <SssAnalyticsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SSS,
            element: (
              <RoleGuard permission={PERMISSIONS.SSS_VIEW}>
                <SssListPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SSS_NEW,
            element: (
              <RoleGuard permission={PERMISSIONS.SSS_CREATE}>
                <SssEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SSS_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.SSS_VIEW}>
                <SssEditPage />
              </RoleGuard>
            ),
          },

          {
            path: ROUTES.CAMPAIGNS,
            element: (
              <RoleGuard permission={PERMISSIONS.CAMPAIGN_VIEW}>
                <CampaignListPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.CAMPAIGN_NEW,
            element: (
              <RoleGuard permission={PERMISSIONS.CAMPAIGN_CREATE}>
                <CampaignEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.CAMPAIGN_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.CAMPAIGN_VIEW}>
                <CampaignEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.WORKFLOW_QUEUE,
            element: (
              <RoleGuard permission={PERMISSIONS.WORKFLOW_QUEUE_VIEW}>
                <WorkflowQueuePage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.MENUS,
            element: (
              <RoleGuard permission={PERMISSIONS.MENU_MANAGE}>
                <MenusPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.MENU_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.MENU_MANAGE}>
                <MenuEditorPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SLIDERS,
            element: (
              <RoleGuard permission={PERMISSIONS.SLIDER_MANAGE}>
                <SlidersPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.BANNERS,
            element: (
              <RoleGuard permission={PERMISSIONS.BANNER_MANAGE}>
                <BannersPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.POPUPS,
            element: (
              <RoleGuard permission={PERMISSIONS.POPUP_MANAGE}>
                <PopupsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.FORMS,
            element: (
              <RoleGuard permission={PERMISSIONS.FORM_VIEW}>
                <FormsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.FORM_SUBMISSIONS,
            element: (
              <RoleGuard permission={PERMISSIONS.FORM_SUBMISSIONS}>
                <FormSubmissionsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.FORM_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.FORM_VIEW}>
                <FormEditorPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.COMPONENTS,
            element: (
              <RoleGuard permission={PERMISSIONS.COMPONENT_MANAGE}>
                <ComponentsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.COMPONENT_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.COMPONENT_MANAGE}>
                <ComponentEditorPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.BRANCHES,
            element: (
              <RoleGuard permission={PERMISSIONS.BRANCH_VIEW}>
                <BranchListPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.BRANCH_NEW,
            element: (
              <RoleGuard permission={PERMISSIONS.BRANCH_MANAGE}>
                <BranchEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.BRANCH_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.BRANCH_VIEW}>
                <BranchEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.CATEGORIES,
            element: (
              <RoleGuard permission={PERMISSIONS.CATEGORY_MANAGE}>
                <CategoriesPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.TAGS,
            element: (
              <RoleGuard permission={PERMISSIONS.TAG_MANAGE}>
                <TagsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.AUTHORS,
            element: (
              <RoleGuard permission={PERMISSIONS.AUTHOR_MANAGE}>
                <AuthorsPage />
              </RoleGuard>
            ),
          },

          {
            path: ROUTES.TRANSLATIONS,
            element: (
              <RoleGuard permission={PERMISSIONS.TRANSLATION_MANAGE}>
                <TranslationsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.LANGUAGES,
            element: (
              <RoleGuard permission={PERMISSIONS.SETTINGS_MANAGE}>
                <LanguagesPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.IP_ALLOWLIST,
            element: (
              <RoleGuard permission={PERMISSIONS.SECURITY_MANAGE}>
                <IpAllowlistPage />
              </RoleGuard>
            ),
          },

          {
            path: ROUTES.WEB_REDIRECTS,
            element: (
              <RoleGuard permission={PERMISSIONS.WEB_REDIRECT}>
                <RedirectMapPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.WEB_OG_PREVIEW,
            element: (
              <RoleGuard permission={PERMISSIONS.WEB_OG_PREVIEW}>
                <OgPreviewPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.WEB_SEO,
            element: (
              <RoleGuard permission={PERMISSIONS.SETTINGS_MANAGE}>
                <SeoSettingsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.KVKK_MY_REQUEST,
            element: <MyKvkkRequestPage />,
          },
          {
            path: ROUTES.PAGES,
            element: (
              <RoleGuard permission={PERMISSIONS.PAGE_VIEW}>
                <PageListPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.PAGE_EDIT,
            element: (
              <RoleGuard permission={PERMISSIONS.PAGE_VIEW}>
                <PageEditPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.ADMIN_USERS,
            element: (
              <RoleGuard permission={PERMISSIONS.ADMIN_USER_MANAGE}>
                <AdminUsersPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.ROLES,
            element: (
              <RoleGuard permission={PERMISSIONS.ROLE_MANAGE}>
                <RolesPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.KVKK_DELETIONS,
            element: (
              <RoleGuard permission={PERMISSIONS.KVKK_DELETION_MANAGE}>
                <KvkkDeletionQueuePage />
              </RoleGuard>
            ),
          },

          {
            path: ROUTES.AUDIT_LOG,
            element: (
              <RoleGuard permission={PERMISSIONS.AUDIT_LOG_VIEW}>
                <AuditLogPage />
              </RoleGuard>
            ),
          },
          { path: ROUTES.PROFILE, element: <ProfilePage /> },
          {
            path: ROUTES.SETTINGS,
            element: (
              <RoleGuard permission={PERMISSIONS.SETTINGS_MANAGE}>
                <SettingsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.SMTP_SETTINGS,
            element: (
              <RoleGuard permission={PERMISSIONS.SETTINGS_MANAGE}>
                <SmtpSettingsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.LDAP_SETTINGS,
            element: (
              <RoleGuard permission={PERMISSIONS.SECURITY_MANAGE}>
                <LdapSettingsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.PROJECTS,
            element: (
              <RoleGuard permission={PERMISSIONS.PROJECT_MANAGE}>
                <ProjectsPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.API_KEYS,
            element: (
              <RoleGuard permission={PERMISSIONS.SECURITY_MANAGE}>
                <ApiKeysPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.MEDIA,
            element: (
              <RoleGuard permission={PERMISSIONS.FILES_READ}>
                <MediaCenterPage />
              </RoleGuard>
            ),
          },
          {
            path: ROUTES.MEDIA_FOLDER,
            element: (
              <RoleGuard permission={PERMISSIONS.FILES_READ}>
                <MediaCenterPage />
              </RoleGuard>
            ),
          },

          { path: ROUTES.FORBIDDEN, element: <ForbiddenPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
